import {ActionStatusState} from 'redux/types/utility';
import {AdditionalHealthPaperwork, AddHealth, User} from 'lib/entities';
import {ActionType} from 'redux/actions/additionalHealthActionTypes';
import * as AuthActionTypes from 'redux/actions/authActionTypes';

type AdditionalHealthPaperworkState = Omit<AdditionalHealthPaperwork, 'completedPaperwork'> & {
    completedPaperwork: Record<string, AddHealth>;
};

export type AddHealthState = {
    addHealth: AddHealth;
    error?: string | Error;
    isSaved?: boolean;
    isEditing?: boolean;
    isSaving?: boolean;
    isUpdating?: boolean;
};

export type AdditionalHealthState = {
    paperwork?: AdditionalHealthPaperworkState;
    additionalHealth: any; //Record<string, AddHealthState>;
    saving: ActionStatusState;
    starting: ActionStatusState;
};

type Handler = (state: AdditionalHealthState, action: Action<any>) => AdditionalHealthState;

function mergeAddHealthWithState(addHealthState: AddHealthState, state: AdditionalHealthState) {
    return state;
}

function mergePaperworkWithState(
    paperwork: AdditionalHealthPaperwork,
    state: AdditionalHealthState,
): AdditionalHealthState {
    const completedPaperwork: Record<string, AddHealth> = JSON.parse(paperwork.completedPaperwork || '{}');

    return {
        ...state,
        paperwork: {
            ...paperwork,
            completedPaperwork,
        },
        additionalHealth: {
            ...state.additionalHealth,
            ...Object.values(completedPaperwork).reduce(
                (carry, addHealth) => ({
                    ...carry,
                    ...addHealth,
                    isSaved: true,
                }),
                {},
            ),
        },
    };
}

export const INITIAL_ADD_HEALTH_STATE = {
    weightChanged: '',
    pcpName: '',
    pcpFirstName: '',
    pcpLastName: '',
    pcpStreet: '',
    pcpState: '',
    pcpCity: '',
    pcpZip: '',
    pcpLastVisited: '',
    pcpVisitReason: '',
    pcpVisitOutcome: '',
    adh_followup: '',
    adh_pcpyesno: '',
    pcpVisitFollowUpAddnDetails: '',
    pcpPhone: '',
    lastDoctorVisitCity: '',
    lastDoctorVisitIsRegularDoctor: false,
    lastDoctorVisitLastVisited: '',
    lastDoctorVisitProviderName: '',
    lastDoctorVisitState: '',
    lastDoctorVisitStreet: '',
    lastDoctorVisitZip: '',
    fatherStillLiving: '',
    fatherAge: '',
    fatherPassedAtAge: '',
    fatherCauseOfPassing: '',
    fatherAdditionalDetails: '',
    motherStillLiving: '',
    motherAge: '',
    motherPassedAtAge: '',
    motherCauseOfPassing: '',
    motherAdditionalDetails: '',
    showPCP: '',
    existingCoverage: '',
    coverageProviderName: '',
    coverageProviderName1: '',
    coverageProviderName2: '',
    coverageProviderNameUnlisted: '',
    coverageProviderNameUnlisted1: '',
    coverageProviderNameUnlisted2: '',
    existingCoverageAmount: '',
    existingCoverageAmount1: '',
    existingCoverageAmount2: '',
    existingPolicyType: '',
    existingPolicyType1: '',
    existingPolicyType2: '',
    confirmExistingCoverageNotReplacementOrExchange: '',
    confirmExistingCoverageNotReplacementOrExchange1: '',
    confirmExistingCoverageNotReplacementOrExchange2: '',
    pastApplication: '',
} as const;

type ObjKey = keyof typeof INITIAL_ADD_HEALTH_STATE;
export type healthStateType = Record<ObjKey, (typeof INITIAL_ADD_HEALTH_STATE)[ObjKey]>;

const ACTION_HANDLERS: Record<ActionType | typeof AuthActionTypes.AUTH_AUTHENTICATE_SUCCESS, Handler> = {
    [ActionType.ADDITIONAL_HEALTH_SAVE]: (state, {payload}: Action<AddHealth>): AdditionalHealthState =>
        mergeAddHealthWithState(
            {
                addHealth: payload,
                isSaving: true,
            },
            state,
        ),
    [ActionType.ADDITIONAL_HEALTH_SAVE_SUCCESS]: (
        state,
        {payload, meta: {shouldEndEditing = false}}: Action<AddHealth>,
    ): AdditionalHealthState =>
        mergeAddHealthWithState(
            {
                addHealth: payload,
                isSaving: false,
                isSaved: true,
                ...(shouldEndEditing && {isEditing: false}),
            },
            state,
        ),
    [ActionType.ADDITIONAL_HEALTH_SAVE_FAILED]: (state, {error, payload}: Action<AddHealth>): AdditionalHealthState => {
        if (state.additionalHealth.addHealth) {
            return mergeAddHealthWithState(
                {
                    addHealth: payload,
                    error,
                    isSaving: false,
                },
                state,
            );
        }

        return state;
    },
    [ActionType.ADDITIONAL_HEALTH_START]: (state): AdditionalHealthState => ({
        ...state,
        starting: {pending: true},
    }),
    [ActionType.ADDITIONAL_HEALTH_START_FAILED]: (state, {error}): AdditionalHealthState => ({
        ...state,
        starting: {pending: false, error},
    }),
    [ActionType.ADDITIONAL_HEALTH_START_SUCCESS]: (
        state,
        {payload}: Action<AdditionalHealthPaperwork>,
    ): AdditionalHealthState => ({
        ...mergePaperworkWithState(payload, state),
        starting: {pending: false},
    }),
    [ActionType.ADDITIONAL_HEALTH_SAVE_PAPERWORK]: (state): AdditionalHealthState => ({
        ...state,
        saving: {pending: true},
    }),
    [ActionType.ADDITIONAL_HEALTH_SAVE_PAPERWORK_FAILED]: (state, {error}): AdditionalHealthState => ({
        ...state,
        saving: {pending: false, error},
    }),
    [ActionType.ADDITIONAL_HEALTH_SAVE_PAPERWORK_SUCCESS]: (state, {payload}): AdditionalHealthState => ({
        ...mergePaperworkWithState(payload, state),
        saving: {pending: false},
    }),
    [AuthActionTypes.AUTH_AUTHENTICATE_SUCCESS]: (state, {payload}: Action<User>): AdditionalHealthState => {
        const paperwork = payload.paperworks.find(
            // Additional Check related to ENG - 592, check backward compatiblity before removing it.
            (p) => p.formSpec?.label === 'additionalHealth' && !!p.completedPaperwork,
        ) as AdditionalHealthPaperwork;

        if (paperwork) return mergePaperworkWithState(paperwork, state);

        return state;
    },
};

const initialState = {
    additionalHealth: {},
    saving: {pending: false},
    starting: {pending: false},
};

// eslint-disable-next-line @typescript-eslint/default-param-last
function additionalHealthReducer(state: AdditionalHealthState = initialState, action: Action): AdditionalHealthState {
    const handler = ACTION_HANDLERS[action.type as ActionType];
    if (handler) return handler(state, action);
    return state;
}

export default additionalHealthReducer;
